import { MemoryHistory } from 'history';
import { ComponentProps, Fragment, useEffect, useMemo, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useHistory, useLocation } from 'react-router';
import Markdown from 'shared/components/Markdown';

import Spacer from 'design-system/components/Spacer';
import Button, { ButtonType } from 'inquiry/components/Button';
import Dialog from 'inquiry/components/Dialog';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { isTerminalStep } from 'inquiry/lib/inquiry';
import {
  closeConfirmCancelDialog,
  openConfirmCancelDialog,
  UiStatus,
} from 'inquiry/redux/actions/ui';
import { getStepStyles } from 'inquiry/redux/selectors/inquiry';
import { onCancel, transitionBack } from 'inquiry/redux/thunk-actions/inquiry';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useToggle from 'lib/hooks/useToggle';
import { colorCss, sizeCss } from 'lib/inquiryStyles';
import { DynamicFlowStep } from 'lib/interfaces/StepsConfig';
import { NavbarLogoDisplay } from 'lib/interfaces/ThemeSerialized';
import { captureMessage } from 'lib/reporting';
import Navbar from 'verify/components/Navbar/Navbar';
import { FromWidgetEvent, HolderClient } from 'verify/lib/clients/HolderClient';
import SessionClientVerify from 'verify/lib/clients/SessionClientVerify';
import { clearMemoryHistory } from 'verify/lib/history';
import { Inquiry } from 'verify/models/inquiry';
import { showMessage } from 'verify/redux/actions/messages';
import { RootState } from 'verify/redux/store';

export const NAVBAR_ID = 'navbar__inquiry-dynamic-flow'; // NB: this id is referenced to override a theme style inside ScannerOverlay

// Only hide for initialization views
const PATHS_TO_HIDE_CLOSE_BUTTON = [
  '/widget',
  '/verify',
  '/inquiry/create-persona/passkey-registration',
  '/inquiry/create-persona/success',
  '/inquiry/verify-with-persona/passkey-authentication',
  '/inquiry/verify-with-persona/success',
  '/inquiry/verify-with-persona/error',
];

/**
 * Hide on start and pending views
 */
const PATHS_TO_HIDE_LOGO = [
  '/widget',
  '/verify',
  '/inquiry/device-handoff/email',
  '/inquiry/device-handoff/prompt',
  '/inquiry/device-handoff/sms',
  '/inquiry/device-handoff/sms/sent',
  '/inquiry/documents/capture',
  '/inquiry/documents/check',
  '/inquiry/documents/pending',
  '/inquiry/documents/prompt',
  '/inquiry/government-id/:step/capture',
  '/inquiry/government-id/:step/check',
  '/inquiry/government-id/:step/prompt',
  '/inquiry/government-id/pending',
  '/inquiry/government-id/select',
  '/inquiry/selfie/center/prompt',
  '/inquiry/selfie/record',
  '/inquiry/selfie/check',
  '/inquiry/selfie-reflection',
  '/inquiry/create-persona/passkey-registration',
  '/inquiry/create-persona/success',
  '/inquiry/verify-with-persona/passkey-authentication',
  '/inquiry/verify-with-persona/success',
  '/inquiry/verify-with-persona/error',
];

/**
 * Paths where user is allowed to trigger a transition back.
 * NOTE: other 'first' paths of combined steps go here.
 */
const PATHS_TO_ALLOW_TRANSITION_BACK = [
  '/inquiry/ui',
  '/inquiry/documents/prompt',
  '/inquiry/government-id/select',
  '/inquiry/selfie/center/prompt',
];

const PATHS_TO_DISALLOW_HISTORY_BACK = [
  '/inquiry/create-persona/passkey-registration',
  '/inquiry/create-persona/success',
  '/inquiry/verify-with-persona/passkey-authentication',
  '/inquiry/verify-with-persona/success',
  '/inquiry/verify-with-persona/error',
];

export const PATHS_TO_HIDE_CONFIRM_CLOSE = ['/inquiry/inactive', '/verify/invalid'];

const ConnectedNavbar = () => {
  const uiStatus = useSelector<RootState, UiStatus>((state) => state.ui.status);
  const inquiry = useSelector<RootState, Inquiry>((state) => state.inquiry.inquiry);
  const isPopupOverlayShown = useSelector<RootState, boolean>((state) => !!state.inquiry.popupUrl);
  const cancelDialogLocalizations = (inquiry.attributes['next-step'] as DynamicFlowStep)?.config
    ?.localizations?.['cancel-dialog'];
  const stepStyles = useSelector(getStepStyles);
  const showConfirmationDialog = useSelector<RootState, boolean>(
    (state) => state.ui.showConfirmCancelDialog,
  );
  const dispatch = useDispatch();

  const { t } = useTranslation();

  const history: MemoryHistory = useHistory();
  const location = useLocation();

  const [
    recentlyChangedPage,
    { enable: enableRecentlyChangedPage, disable: disableRecentlyChangedPage },
  ] = useToggle(false);

  useEffect(() => {
    enableRecentlyChangedPage();
    // Our longest animation is 400ms. See the constants in AnimatedPage.
    const timeout = setTimeout(disableRecentlyChangedPage, 500);

    return () => {
      clearTimeout(timeout);
    };
  }, [location]);

  /**
   * Regular, history-based back is permitted if history is not empty AND the
   * current step is a combined step.
   */
  const historyBackPermitted = useMemo<boolean>(() => {
    if (history.index == null || history.index <= 0) {
      return false;
    }

    // Don't allow history back if on the first page of a combined step.
    if (
      PATHS_TO_ALLOW_TRANSITION_BACK.some((path) =>
        matchPath(location.pathname, { path, exact: true, strict: false }),
      )
    ) {
      return false;
    }

    if (
      PATHS_TO_DISALLOW_HISTORY_BACK.some((path) =>
        matchPath(location.pathname, { path, exact: true, strict: false }),
      )
    ) {
      return false;
    }

    return true;
  }, [history.index, location.pathname]);

  const transitionBackPermitted = useMemo<boolean>(() => {
    if (!inquiry.attributes['next-step'].config['back-step-enabled']) {
      return false;
    }

    // History back is allowed when history is NOT empty and when NOT on the
    // first step of a combined step.
    // In all other cases, we can transition back if a back step is provided.
    return !historyBackPermitted;
  }, [inquiry, historyBackPermitted]);

  const { error, callback: handleBack } = useApiCallback(async () => {
    if (transitionBackPermitted) {
      dispatch(transitionBack(inquiry));
    } else if (historyBackPermitted) {
      history.goBack();
    } else {
      captureMessage(
        'Tried to navigate back when neither transition nor history back permitted',
        'warning',
      );
    }
  }, [location.pathname, transitionBackPermitted, historyBackPermitted, inquiry]);

  /**
   * Don't allow back navigation so close to route changing.
   * This can lead to weird issues with components not being unmounted/reinitialized properly.
   */
  const lastLocationChangeTime = useRef(Date.now());
  useEffect(() => {
    lastLocationChangeTime.current = Date.now();
  }, [location.pathname]);

  useApiErrorHandler({
    error,
    onError: () => {
      dispatch(showMessage('Unexpected error, please try again.'));
    },
    onNetworkError: () => dispatch(showMessage('Unexpected error, please try again.')),
  });

  const handleClose = () => {
    if (
      PATHS_TO_HIDE_CONFIRM_CLOSE.some((path) =>
        matchPath(location.pathname, { path, exact: false, strict: false }),
      )
    ) {
      handleConfirmClose();
    } else {
      dispatch(openConfirmCancelDialog());
    }
  };

  /**
   * @see VerifyExitRoute.tsx handleSubmit()
   */
  const handleConfirmClose = () => {
    onCancel();

    HolderClient.onEvent(
      FromWidgetEvent.Exit,
      {
        // Unlike in Inqv2, inquiries are created once the widget is open, so `inquiry` will never be null.
        inquiryId: inquiry.id,
        sessionToken: SessionClientVerify.accessToken,
      },
      { inquiry },
    );

    // Prevent back button from working if reopening widget.
    clearMemoryHistory(history);

    // Redirect to start of inquiry flow, so if the user re-opens the widget
    // they don't see the exit page.
    //
    // Use history.push('/widget') instead of HolderClient.redirectToStart
    // because only the widget shows the exit page.
    history.push('/widget');

    dispatch(closeConfirmCancelDialog());
  };

  const uiIsPolling = uiStatus === UiStatus.Blocked;
  const uiIsTransitioning = uiStatus === UiStatus.Transitioning;

  const showCloseButton =
    inquiry.attributes['next-step'].config['cancel-button-enabled'] &&
    !HolderClient.isHosted &&
    !HolderClient.isInline &&
    !isTerminalStep(inquiry) &&
    !PATHS_TO_HIDE_CLOSE_BUTTON.some((path) =>
      matchPath(location.pathname, { path, exact: true, strict: false }),
    ) &&
    !uiIsPolling &&
    !isPopupOverlayShown; // don't show close button when we are displaying the popup overlay when a popup window is open during reusable persona flow

  const showBackButton =
    (transitionBackPermitted || historyBackPermitted) &&
    !uiIsPolling &&
    // reusable personas popup can sometimes push error states onto the stack
    // if the error state occurs before the inquiry is routed to the passkey
    // route, then going back will cause the popup to render the main flow
    // always prevent going back to prevent this issue
    !HolderClient.isReusablePersonaPopup;

  const logo = inquiry.attributes['logo'];
  let logoDisplay = logo?.['display'];
  if (
    !logoDisplay ||
    PATHS_TO_HIDE_LOGO.some((path) =>
      matchPath(location.pathname, { path, exact: true, strict: false }),
    )
  ) {
    logoDisplay = NavbarLogoDisplay.None;
  }

  const navbarStyles: React.ComponentProps<typeof Navbar>['styles'] = {
    // Currently defaults to modal background color. If needed, add a dedicated SST attribute.
    backgroundColor: colorCss(stepStyles?.['background-color']?.header?.base),
    borderColor: colorCss(stepStyles?.['border-color']?.header?.base),
    borderWidth: sizeCss(stepStyles?.['border-width']?.header?.base?.bottom),
  };

  const modalStyles: ComponentProps<typeof Dialog>['styles'] = {
    backgroundColor: stepStyles?.['background-color']?.base?.base,
    padding: stepStyles?.padding?.modal?.base,
    border: {
      radius: stepStyles?.['border-radius']?.modal?.base,
    },
  };

  return (
    <>
      <Dialog
        isOpen={showConfirmationDialog}
        onClose={() => dispatch(closeConfirmCancelDialog())}
        styles={modalStyles}
        data-test='confirm-exit-dialog'
        aria-labelledby='confirm-exit-dialog__title'
        aria-describedby='confirm-exit-dialog__description'
      >
        <ThemedTitle
          id='confirm-exit-dialog__title'
          styles={stepStyles?.['title-style']?.base.base}
        >
          <Markdown>{cancelDialogLocalizations?.title ?? t('exit.title')}</Markdown>
        </ThemedTitle>

        <ThemedParagraph
          id='confirm-exit-dialog__description'
          styles={stepStyles?.['text-style']?.base.base}
        >
          <Markdown>{cancelDialogLocalizations?.prompt ?? t('exit.prompt')}</Markdown>
        </ThemedParagraph>

        <Spacer height={2} />

        <Button
          id='confirm-exit-dialog__button--close'
          data-test='confirm-exit-dialog__button--close'
          buttonType={ButtonType.Primary}
          styles={stepStyles?.['button-primary-style']?.base.base}
          onClick={handleConfirmClose}
        >
          {cancelDialogLocalizations?.['btn-submit'] ?? t('exit.btn-submit')}
        </Button>

        {/* Assume spacing is specified by styles if present */}
        {stepStyles == null && <Spacer height={1.5} />}

        <Button
          id='confirm-exit-dialog__button--resume'
          data-test='confirm-exit-dialog__button--resume'
          buttonType={ButtonType.Secondary}
          styles={stepStyles?.['button-secondary-style']?.base.base}
          onClick={() => dispatch(closeConfirmCancelDialog())}
        >
          {cancelDialogLocalizations?.['btn-resume'] ?? (
            <Trans
              // Only use Trans to reuse interpolated i18n
              i18nKey='exit.or-go-back'
              defaults='Or <0>click here to resume</0>'
              components={[<Fragment key={0}>click here to resume</Fragment>]}
            >
              Or click here to resume
            </Trans>
          )}
        </Button>
      </Dialog>
      <Navbar
        id={NAVBAR_ID}
        data-test='navbar__inquiry-dynamic-flow'
        onBack={handleBack}
        onClose={handleClose}
        styles={navbarStyles}
        showBackButton={showBackButton}
        showCloseButton={showCloseButton}
        colorIconHeader={colorCss(stepStyles?.['text-color']['header-button'].base)}
        logoContentType={logo?.['content-type']}
        logoDisplay={logoDisplay}
        logoUrl={logo?.['url']}
        loading={
          uiIsTransitioning ||
          // Meant to disable jank from quickly navigating forwards and
          // backwards, or accidentally navigating back many times. Because
          // of animations, this can cause weird race conditions where
          // components are not properly remounted when navigating too fast.
          recentlyChangedPage
        }
      />
    </>
  );
};

export default ConnectedNavbar;
